import { useEffect, useState } from "react";
import { useGetLocalizedText } from "../../../hooks/localization";
import SupportDialog from "../../common/dialogs/SupportDialog";
import UserLanguageSelect from "../../common/UserLanguageSelect";
import logoImg from "../../../assets/logo_with_text.svg";
import { useEvent } from "../../../hooks/event";
import { useRoom, useRoomId } from "../../context/room/room";
import Loader from "../../common/Loader";
import { useAxios } from "../../../hooks/axios";
import api from "../../../hooks/api";
import useUser from "../../../hooks/user";
import { AxiosError } from "axios";
import { toast } from "react-toastify";
import classNames from "./EventWaitingRoom.module.css";
import Button from "../../common/Button";
import EventTitles from "./EventTitles";
import { useLogEvent } from "../../../hooks/log";

// Waiting room for event viewers
function EventWaitingRoom() {
  const { room, loading } = useRoom();

  const [, setEventState] = useEvent();
  const getLocalizedText = useGetLocalizedText();

  const [showSupportDialog, setShowSupportDialog] = useState(false);

  const axios = useAxios();
  const getToken = api.useGenerateLivekitToken(axios);
  const roomId = useRoomId();
  const [user] = useUser();

  const [canJoin, setCanJoin] = useState<
    "event_ended" | "event_not_started" | true
  >(true);
  useEffect(() => {
    const checkIfStarted = () => {
      if (!room || !room.start_time || !room.end_time) {
        return;
      }

      const now = new Date();
      let start = new Date(room.start_time);
      let end = new Date(room.end_time);

      // KEEP IN SYNC WITH api-gateway/src/routes/v1/auth.ts (POST /v1/auth/livekitToken)
      if (room.reoccurring) {
        const durationInMillis = end.getTime() - start.getTime();

        // Re-anchor the original start time onto yesterday/today/tomorrow
        // and use whichever occurrence is actually closest to `now`, so
        // events spanning midnight (or just near an hour boundary) resolve
        // to the right day instead of always snapping to "today".
        const candidateStarts = [-1, 0, 1].map((dayOffset) =>
          new Date(
            now.getFullYear(),
            now.getMonth(),
            now.getDate() + dayOffset,
            start.getHours(),
            start.getMinutes(),
            start.getSeconds(),
          ).getTime(),
        );
        const startTime = candidateStarts.reduce((closest, candidate) =>
          Math.abs(candidate - now.getTime()) <
          Math.abs(closest - now.getTime())
            ? candidate
            : closest,
        );
        start = new Date(startTime);
        end = new Date(startTime + durationInMillis);
      }

      // Add a 30 min buffer
      if (!room.available_24_7) {
        if ((start.getTime() - now.getTime()) / (1000 * 60) > 30) {
          setCanJoin("event_not_started");
          return;
        }
        if ((now.getTime() - end.getTime()) / (1000 * 60) > 30) {
          setCanJoin("event_ended");
          return;
        }
      }
      setCanJoin(true);
    };

    checkIfStarted();

    // Check every 5s
    const interval = setInterval(checkIfStarted, 5 * 1000);

    return () => {
      clearInterval(interval);
    };
  }, [room]);

  const logEvent = useLogEvent();

  if (!room || loading) {
    return <Loader />;
  }

  return (
    <>
      <SupportDialog
        open={showSupportDialog}
        onOpenChange={setShowSupportDialog}
      />
      <div className={classNames.EventWaitingRoom}>
        <img src={logoImg} alt="Langfinity" />

        <EventTitles room={room} hideParticipants={true} />

        <div className={classNames.LanguageSelectAndJoin}>
          <div>
            <h3>Choose your language</h3>
            <UserLanguageSelect
              color="white"
              borderColor="dark-gray"
              backgroundColor="dark-gray"
              languages={room.languages}
            />
          </div>
          <Button
            disabled={canJoin !== true}
            onClick={async () => {
              logEvent({
                type: "click_join_as_attendee",
                details: {
                  room_id: roomId,
                  language: user.metadata.selectedLanguage,
                },
              });
              try {
                await getToken({
                  room: roomId,
                  identity: user.livekitId,
                });
                setEventState((prevState) => ({ ...prevState, joined: true }));
              } catch (error) {
                if (!(error instanceof AxiosError)) {
                  throw error;
                }
                if (error.status !== 403) {
                  throw error;
                }
                if (error.response?.data.message === "EVENT_NOT_STARTED") {
                  return toast(
                    <div className="Toast">
                      <div>{"Event has not yet started"}</div>
                    </div>,
                  );
                }
                if (error.response?.data.message === "EVENT_ENDED") {
                  return toast(
                    <div className="Toast">
                      <div>{"This event has ended, thanks for joining us"}</div>
                    </div>,
                  );
                }
                throw error;
              }
            }}
          >
            {getLocalizedText("JOIN")}
          </Button>

          {canJoin === "event_ended" ? (
            <p className={classNames.CantJoin}>
              This event has ended, thanks for joining us!
            </p>
          ) : undefined}
          {canJoin === "event_not_started" ? (
            <p className={classNames.CantJoin}>
              This event hasn't started yet, stay tuned!
            </p>
          ) : undefined}

          <p>
            Having issues?{" "}
            <span
              onClick={() => setShowSupportDialog(true)}
              className={classNames.SupportToggle}
            >
              Contact support
            </span>
          </p>
        </div>

        <div className={classNames.Footer}>
          <p>
            <a
              target="_blank"
              rel="noreferrer"
              href="https://byrdhouse.notion.site/byrdhouse/Privacy-Policy-c71b7c579187429da97f8aec36b1585e"
            >
              {getLocalizedText("PRIVACY_POLICY")}
            </a>{" "}
            /{" "}
            <a
              target="_blank"
              rel="noreferrer"
              href="https://byrdhouse.notion.site/byrdhouse/Terms-of-Service-7d7aa2e356ef4f22bec82a8d885fdc4d"
            >
              {getLocalizedText("TERMS_OF_SERVICE")}
            </a>
          </p>
          <p>©2025 BYRDHOUSE INC</p>
        </div>
      </div>
    </>
  );
}

export default EventWaitingRoom;
